import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './primitives.tsx';

/**
 * Successes only.
 *
 * A failure must open the error dialog — a toast can be missed, and an operator
 * who misses "the withdrawal was NOT released" acts on a false belief. Anything
 * that did not happen goes to `useErrorDialog`.
 */
interface ToastItem {
  id: number;
  message: string;
}

interface ToastApi {
  toast: (message: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>.');
  return ctx;
}

const DISMISS_AFTER_MS = 4600;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const toast = useCallback((message: string) => {
    const id = nextId.current++;
    setItems((cur) => [...cur, { id, message }]);
    setTimeout(() => setItems((cur) => cur.filter((t) => t.id !== id)), DISMISS_AFTER_MS);
  }, []);

  const api = useMemo<ToastApi>(() => ({ toast }), [toast]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {/* polite, not assertive: a confirmation should not interrupt what the
          user is reading or typing. */}
      <div className="toast-region" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="toast">
            <Icon name="check-circle" style={{ color: 'var(--portal-accent)' }} />
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
